import React from "react";
import styled from "styled-components";

interface Column<T> {
  key: keyof T | string;
  header: string;
  align?: "left" | "right" | "center";
  render?: (value: any, row: T) => React.ReactNode;
  width?: string | number;
}

interface CustomTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  rowKey?: keyof T | ((row: T) => string | number);
}

const TableWrapper = styled.div`
  width: 100%;
  border: 1px solid #e39435;
  border-radius: 8px;
  overflow-y: hidden;
  background: #ffffff;

  @media (max-width: 767px) {
    overflow-x: auto;
    border-radius: 0;
    border: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 600px; 

  @media (max-width: 1199px) and (min-width: 768px) {
    table-layout: auto;
    min-width: 100%;
  }

  @media (max-width: 767px) {
    min-width: 100%;
    table-layout: auto;
  }
`;

const Thead = styled.thead`
  border-bottom: 1px solid #eeeeee;
  padding: 16px;
  display: table-header-group; // Ensure header is visible on desktop

  @media (max-width: 767px) {
    display: none; // Hide header on mobile, or keep it based on your preference
  }
`;

const Th = styled.th<{ $width?: string | number, $align?: "left" | "right" | "center" }>`
  padding: 16px;
  text-align: ${({ $align = "left" }) => $align};
  font-weight: 500;
  height: 60px;
  font-size: 16px;
  color: #b5b7c0;
  min-width: 120px;
  ${({ $width }) =>
    $width && `width: ${typeof $width === "number" ? `${$width}px` : $width}`};

  &:first-child {
    padding-left: 24px;
  }

  &:last-child {
    padding-right: 24px;
  }

  @media (max-width: 1024px) and (min-width: 768px) {
    padding: 12px 8px;
    font-size: 14px;
    height: 50px;

    &:first-child {
      padding-left: 16px;
    }

    &:last-child {
      padding-right: 16px;
    }
  }

  @media (max-width: 767px) {
    padding: 8px 12px;
    font-size: 14px;

    &:first-child {
      padding-left: 16px;
    }

    &:last-child {
      padding-right: 16px;
    }
  }
`;

const Tbody = styled.tbody`
  @media (max-width: 767px) {
    display: block;
  }
`;

const Tr = styled.tr<{ $clickable?: boolean }>`
  border-bottom: 1px solid #eeeeee;
  transition: background-color 0.2s;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ $clickable }) =>
      $clickable ? "#fef6eeff" : "transparent"};
  }

  @media (max-width: 767px) {
    display: block;
    margin-bottom: 16px;
    border: 1px solid #ffd39d;
    border-radius: 8px;
    padding: 16px;

    &:last-child {
      border-bottom: 1px solid #ffd39d !important;
    }
  }
`;

const Td = styled.td<{ $align?: "left" | "right" | "center" }>`
  padding: 16px;
  font-size: 16px;
  text-align: ${({ $align = "left" }) => $align};
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:first-child {
    padding-left: 24px;
    font-weight: 700;

    // Fix alignment for content with images and text
    div {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }

  &:last-child {
    padding-right: 24px;
  }

  @media (max-width: 1024px) and (min-width: 768px) {
    padding: 12px 8px;
    font-size: 14px;
    white-space: normal;

    &:first-child {
      padding-left: 16px;
    }

    &:last-child {
      padding-right: 16px;
      min-width: 100px; // Ensure View Details has enough space
    }
  }

  @media (max-width: 767px) {
    display: block;
    padding: 0;
    white-space: normal;
    text-align: left !important;
    border: none;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }

    &:before {
      content: attr(data-label);
      font-weight: 600;
      color: #b5b7c0;
      display: block;
      margin-bottom: 4px;
      font-size: 14px;
    }

    &:first-child:before {
      display: none; // Don't show label for first column
    }
  }
`;

const EmptyState = styled(Td).attrs({ colSpan: 100 })`
  text-align: center;
  padding: 40px 16px;
  color: #ffd39d;

  @media (max-width: 767px) {
    display: table-cell;

    &:before {
      display: none;
    }
  }
`;

// Mobile-specific styles for the first column content
const MobileFirstColumn = styled.div`
  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eeeeee;
  }
`;

function CustomTable<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  emptyMessage = "No data available",
  className,
  rowKey = "id",
}: CustomTableProps<T>) {
  const getRowKey = React.useCallback(
    (row: T, index: number) => {
      return typeof rowKey === "function"
        ? rowKey(row)
        : row[rowKey as keyof T] ?? index;
    },
    [rowKey]
  );

  return (
    <TableWrapper className={className}>
      <Table>
        <Thead>
          <tr>
            {columns.map((column) => (
              <Th
                key={column.key as string}
                $align={column.align}
                $width={column.width}
              >
                {column.header}
              </Th>
            ))}
          </tr>
        </Thead>
        <Tbody>
          {data.length > 0 ? (
            data.map((row, rowIndex) => (
              <Tr
                key={getRowKey(row, rowIndex)}
                $clickable={!!onRowClick}
              >
                {columns.map((column, colIndex) => {
                  const value = row[column.key as keyof T];
                  return (
                    <Td
                      key={column.key as string}
                      $align={column.align}
                      data-label={column.header}
                    >
                      {colIndex === 0 ? (
                        <MobileFirstColumn>
                          {column.render ? column.render(value, row) : value}
                        </MobileFirstColumn>
                      ) : column.render ? (
                        column.render(value, row)
                      ) : (
                        value
                      )}
                    </Td>
                  );
                })}
              </Tr>
            ))
          ) : (
            <tr>
              <EmptyState colSpan={columns.length}>{emptyMessage}</EmptyState>
            </tr>
          )}
        </Tbody>
      </Table>
    </TableWrapper>
  );
}

export default CustomTable;
